//!#system	

<template>
	<v-card :loading="isLoading">
		<v-card-title>
			<span class="headline">Data Transformation Template</span>
		</v-card-title>
		<v-card-text>
			<v-autocomplete
				v-model="model.bucket"
				:items="buckets"
				label="Choose the bucket"
				@change="chosenBucket"
			></v-autocomplete>
			<v-checkbox
				v-model="sendOneObjectEach"
				label="Optimize for sending one request at a time"
				@change="chosenBucket"
			/>
			<v-row no-gutters v-if="model.bucket">
				<v-col
					cols="12"
					lg="6"
					sm="12"
				>
					<div v-show="!sendOneObjectEach">
						<div>
							Header template
						</div>
						<iframe 
							src="/ui/editor.html?property=headerTemplate" 
							ref="headerTemplate" 
							width="100%"
							height="100" 
							frameBorder="0"
						/>
					</div>
					<div>
						Data template
					</div>
					<iframe 
						src="/ui/editor.html?property=template" 
						ref="template" 
						width="100%"
						height="928" 
						frameBorder="0"
					/>
					<div v-show="!sendOneObjectEach">
						<div>
							Footer template
						</div>
						<iframe 
							src="/ui/editor.html?property=footerTemplate" 
							ref="footerTemplate" 
							width="100%"
							height="100" 
							frameBorder="0"
						/>
					</div>
				</v-col>
				<v-col
					cols="12"
					lg="6"
					sm="12"

				>	<div style="margin-left: 10px;">
						<v-textarea
							v-model="objectDataToDisplay"
							label="Sample data"
							:rows="40"
							filled
							background-color="blue lighten-5"
						/>
					</div>
				</v-col>
			</v-row>
			<v-alert
				dense
				type="info"
			>
				Templates are broken into 3 parts: Header, Data, and Footer.  The Header and Footer templates will be used once each - for the top and bottom of your transformation, while the Data template may be used many times for large result sets.  When doing transformations for each item, omit the Header and Footer templates.
			</v-alert>
			<handlebars-docs type="server" />
		</v-card-text>
		<v-card-actions>
			<v-spacer></v-spacer>
			<v-btn text @click="closeDialog()">Exit</v-btn>
			<v-btn color="primary" :disabled="!canSubmit" @click="doAction()">{{submitText}}</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import HandlebarsDocs from '@/components/uidocs/HandlebarsDocs';
import apiService from '@/apiService';
import series from 'to-series';

export default {
	name: 'DataTransformationTemplateOptions',
	components: {
		HandlebarsDocs
	},
	props: {
		routeInfo: {
			type: Object
		},
		detail: { // Optional starter object
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			model: {
				bucket: this.detail.bucket,
				headerTemplate: this.detail.headerTemplate,
				template: this.detail.template,
				footerTemplate: this.detail.footerTemplate,
				tags: this.detail.tags
			},
			lastChosenBucket: '',
			isLoading: false,
			namespaces: null,
			buckets: [],
			blankObject: null,
			sampleDataObject: null,
			objectDataToDisplay: null,
			sendOneObjectEach: this.detail.tags.includes('One-Object-Each') || false
		}
	},
	computed: {
		submitText() {
			return 'Save template';
		},
		canSubmit() {
			return true;
		},
		objectList() {
			const list = [];
			Object.keys(this.namespaces).forEach(key => {
				this.namespaces[key].forEach(objectName => {
					list.push(key + '/' + objectName);
				});
			});
			return list;
		}
	},
	methods: {
		updateCode() {
			if (!this.$refs.headerTemplate || !this.$refs.template || !this.$refs.footerTemplate) {
				console.log('No editor yet. Waiting');
				return setTimeout(this.updateCode, 200);
			}
			if (!this.model.template) {
				console.log('No template to send to the child');
				return setTimeout(this.updateCode, 200);
			}
			this.sendMessageToEditor('headerTemplate', this.model);
			this.sendMessageToEditor('template', this.model);
			this.sendMessageToEditor('footerTemplate', this.model);
			this.$refs.headerTemplate.onload = () => {
				this.sendMessageToEditor('headerTemplate', this.model);
			};
			this.$refs.template.onload = () => {
				this.sendMessageToEditor('template', this.model);
			};
			this.$refs.footerTemplate.onload = () => {
				this.sendMessageToEditor('footerTemplate', this.model);
			};
		},
		sendMessageToEditor(editor, message) {
			console.log('sendMessageToEditor', this.$refs);
			this.$refs[editor].contentWindow.postMessage(message, '*');
		},
		doAction() {
			// save the data
			if (this.sendOneObjectEach) {
				// Need to empty the header and footer if sending one at a time
				this.model.headerTemplate = '';
				this.model.footerTemplate = '';
			}
			apiService.update({
				...this.routeInfo,
				body: {
					...this.detail,
					...this.model
				}
			}, (err, response, meta) => {
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: meta.validation ? meta.validation.messages.join('\n') : err  
					});
				} else {
					this.$store.dispatch('app/applyObjectUpdated', {
						show: true,
						text: 'Your template has been updated.'
					});
					this.$emit('refresh');
				}
			})
		},
		createStarterTemplate(objectName) {
			return '{{#each ' + objectName + '}}\r\n' +
				'   \r\n' +
			'{{/each}}';
		},
		chosenBucket() {
			if (!this.model.bucket) return;
			let [ namespace, objectName ] = this.model.bucket.split('/');
			let starterTemplate = this.createStarterTemplate(objectName);
			if (!this.model.template) {
				this.model.template = starterTemplate;
			}
			// Try to figure out if the stater template was changed
			// If not changed then we can update it to the new chose bucket
			if (this.lastChosenBucket) {
				let lastChosenBucketObjectName = this.lastChosenBucket.split('/')[1],
					lastStarterTemplate = this.createStarterTemplate(lastChosenBucketObjectName);

				if (this.model.template === lastStarterTemplate) {
					this.model.template = starterTemplate;
				}
			}
			if (this.sendOneObjectEach && !this.model.tags.includes('One-Object-Each')) {
				this.model.tags.push('One-Object-Each');
			} else if (!this.sendOneObjectEach && this.model.tags.includes('One-Object-Each')) {
				this.model.tags = this.model.tags.filter(t => t !== 'One-Object-Each');
			}
			series()
				.first(cb => {
					this.getBlank(cb);
				})
				.next(cb => {
					this.updateCode();
					return cb();
				})
				.next(cb => {
					this.getSampleData(cb);
				})
				.end(() => {
					this.lastChosenBucket = this.model.bucket;
				});
		},
		getNamespaces(done) {
			apiService.call({
				method: 'get',
				path: '/_sys/internals/rules/get-app-namespaces'
			}, (err, response) => {
				this.namespaces = response;
				return done(err);
			});
		},
		getBlank(done) {
			if (!this.model.bucket) return;
			let [ namespace, pluralName ] = this.model.bucket.split('/');
			apiService.blank({ namespace, pluralName }, (err, response, meta) => {
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: meta.validation ? meta.validation.messages.join('\n') : err 
					});
					return done();
				}
				if (this.sendOneObjectEach) {
					this.blankObject = JSON.stringify(response, null, 2);
					this.objectDataToDisplay = this.blankObject;
				} else {
					this.blankObject = {};
					this.blankObject[pluralName] = [response];
					this.blankObject = JSON.stringify(this.blankObject, null, 2);
					this.objectDataToDisplay = this.blankObject;
				}
				return done();
			});
		},
		getSampleData(done) {
			if (!this.model.bucket) return;
			let [ namespace, pluralName ] = this.model.bucket.split('/');
			apiService.list({ namespace, pluralName, filters: { _limit_: 1 } }, (err, response, meta) => {
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: meta.validation ? meta.validation.messages.join('\n') : err 
					});
					return done();
				}
				if (response && response.length > 0) {
					apiService.detail({ namespace, pluralName, id: response[0].id }, (err, response, meta) => {
						if (err) {
							this.$store.dispatch('app/applyObjectError', {
								show: true,
								text: meta.validation ? meta.validation.messages.join('\n') : err 
							});
							return done();
						}
						if (this.sendOneObjectEach) {
							this.sampleDataObject = {};
							this.sampleDataObject = JSON.stringify(response, null, 2);
							this.objectDataToDisplay = this.sampleDataObject;
						} else {
							this.sampleDataObject = {};
							this.sampleDataObject[pluralName] = [response];
							this.sampleDataObject = JSON.stringify(this.sampleDataObject, null, 2);
							this.objectDataToDisplay = this.sampleDataObject;
						}
						return done();
					});
				}
			});
		},
		getBuckets(done) {
			this.$store.dispatch('app/buckets', { 
				options: {}, 
				callback: (err, buckets) => {
					this.buckets = buckets;
					return done(err);
				} 
			});
		},
		getInitialData() {
			this.isLoading = true;
			series()
				.first(cb => {
					this.getBuckets(cb);
				})
				.end(() => {
					this.isLoading = false;
					if (this.model.bucket) {
						this.lastChosenBucket = this.model.bucket;
						this.chosenBucket();
					}
				});
		},
		closeDialog() {
			this.$emit('close');
		}
	},
	mounted() {
		window.onmessage = (e) => {
			if (e.data.template) {
				this.model.template = e.data.template;
			}
			if (e.data.headerTemplate) {
				this.model.headerTemplate = e.data.headerTemplate;
			}
			if (e.data.footerTemplate) {
				this.model.footerTemplate = e.data.footerTemplate;
			}
		};
	},
	created() {
		this.getInitialData();
	}
};
</script>

<style scoped>
.code-wrapper pre {
	all: initial;
}
.code-wrapper code {
	all: initial;
}

code[class*="language-"],
pre[class*="language-"] {
	color: black;
	background: none;
	text-shadow: 0 1px white;
	font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
	font-size: 1em;
	text-align: left;
	white-space: pre;
	word-spacing: normal;
	word-break: normal;
	word-wrap: normal;
	line-height: 1.5;

	-moz-tab-size: 4;
	-o-tab-size: 4;
	tab-size: 4;

	-webkit-hyphens: none;
	-moz-hyphens: none;
	-ms-hyphens: none;
	hyphens: none;
}

pre[class*="language-"]::-moz-selection, pre[class*="language-"] ::-moz-selection,
code[class*="language-"]::-moz-selection, code[class*="language-"] ::-moz-selection {
	text-shadow: none;
	background: #b3d4fc;
}

pre[class*="language-"]::selection, pre[class*="language-"] ::selection,
code[class*="language-"]::selection, code[class*="language-"] ::selection {
	text-shadow: none;
	background: #b3d4fc;
}

@media print {
	code[class*="language-"],
	pre[class*="language-"] {
		text-shadow: none;
	}
}

/* Code blocks */
pre[class*="language-"] {
	padding: 1em;
	margin: .5em 0;
	overflow: auto;
}

:not(pre) > code[class*="language-"],
pre[class*="language-"] {
	background: #f5f2f0;
}

/* Inline code */
:not(pre) > code[class*="language-"] {
	padding: .1em;
	border-radius: .3em;
	white-space: normal;
}

.token.comment,
.token.prolog,
.token.doctype,
.token.cdata {
	color: slategray;
}

.token.punctuation {
	color: #999;
}

.token.namespace {
	opacity: .7;
}

.token.property,
.token.tag,
.token.boolean,
.token.number,
.token.constant,
.token.symbol,
.token.deleted {
	color: #905;
}

.token.selector,
.token.attr-name,
.token.string,
.token.char,
.token.builtin,
.token.inserted {
	color: #690;
}

.token.operator,
.token.entity,
.token.url,
.language-css .token.string,
.style .token.string {
	color: #9a6e3a;
	background: hsla(0, 0%, 100%, .5);
}

.token.atrule,
.token.attr-value,
.token.keyword {
	color: #07a;
}

.token.function,
.token.class-name {
	color: #DD4A68;
}

.token.regex,
.token.important,
.token.variable {
	color: #e90;
}

.token.important,
.token.bold {
	font-weight: bold;
}
.token.italic {
	font-style: italic;
}

.token.entity {
	cursor: help;
}

</style>